import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Gift, Users, Trophy, Wallet, Share2, Copy, Check, ChevronRight, 
  ArrowRight, Star, TrendingUp, Award, IndianRupee, Clock, UserPlus,
  Phone, Mail, MessageCircle, Send, CheckCircle, XCircle, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReferAndEarn() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [copied, setCopied] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [upiId, setUpiId] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get public settings and leaderboard
      const [settingsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/referral/settings`),
        axios.get(`${API}/referral/leaderboard`)
      ]);
      setSettings(settingsRes.data);
      setLeaderboard(leaderboardRes.data);

      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        setIsLoggedIn(true);
        try {
          const walletRes = await axios.get(`${API}/referral/my-wallet`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setWallet(walletRes.data.wallet);
          setReferrals(walletRes.data.referrals);
          setUpiId(walletRes.data.wallet?.upi_id || '');
        } catch (err) {
          console.error('Error fetching wallet:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (wallet?.referral_code) {
      navigator.clipboard.writeText(wallet.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = () => {
    const message = `Hey! Join Officium Connect using my referral code: ${wallet?.referral_code} and we both can earn rewards! 🎉\n\nSign up now: ${window.location.origin}/login?ref=${wallet?.referral_code}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!upiId) {
      toast.error('Enter your UPI ID');
      return;
    }

    setWithdrawing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/referral/withdrawal-request?amount=${withdrawAmount}&upi_id=${upiId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Withdrawal request submitted!');
      setWithdrawDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setWithdrawing(false);
    }
  };

  const statusColors = {
    'Pending': 'bg-yellow-100 text-yellow-800',
    'Registered': 'bg-blue-100 text-blue-800',
    'Telephone_Cleared': 'bg-green-100 text-green-800',
    'Hired': 'bg-emerald-100 text-emerald-800',
    'Rewarded': 'bg-purple-100 text-purple-800',
    'Rejected': 'bg-red-100 text-red-800'
  };

  const rewardStatusColors = {
    'Pending': 'bg-gray-100 text-gray-800',
    'Eligible': 'bg-green-100 text-green-800',
    'Credited': 'bg-emerald-100 text-emerald-800',
    'Cancelled': 'bg-red-100 text-red-800'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 opacity-90"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMThjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            {/* Floating Icons */}
            <div className="hidden sm:block absolute top-10 left-10 animate-bounce">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Gift className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="hidden sm:block absolute top-20 right-20 animate-pulse">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <IndianRupee className="h-5 w-5 text-white" />
              </div>
            </div>
            
            <Badge className="mb-4 bg-white/20 text-white border-white/30 px-4 py-1.5 text-sm">
              🎉 Earn Rewards for Every Successful Referral
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              {settings?.hero_title || 'Refer & Earn Rewards'}
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
              {settings?.hero_description || 'Invite friends to join Officium Connect and earn rewards when they get selected!'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {isLoggedIn && wallet ? (
                <>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                    <span className="text-white/80">Your Code:</span>
                    <span className="text-xl font-bold text-white tracking-wider">{wallet.referral_code}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={copyReferralCode}
                      className="text-white hover:bg-white/20"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button 
                    onClick={shareOnWhatsApp}
                    className="bg-green-500 hover:bg-green-600 text-white gap-2 rounded-xl px-6"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Share on WhatsApp
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-white text-indigo-600 hover:bg-gray-100 gap-2 rounded-xl px-8 py-6 text-lg"
                >
                  Get Started <ArrowRight className="h-5 w-5" />
                </Button>
              )}
            </div>
            
            {/* Reward Amount Badge - Only show if reward > 0 */}
            {settings?.reward_amount > 0 && (
              <div className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full px-6 py-3 shadow-lg">
                <Trophy className="h-6 w-6 text-white" />
                <span className="text-white font-bold text-lg">
                  Earn ₹{settings.reward_amount} per successful referral!
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Banner Image - Only show if set */}
        {settings?.banner_image_url && (
          <div className="absolute bottom-20 left-0 right-0 px-4">
            <div className="max-w-4xl mx-auto">
              <img 
                src={`${API.replace('/api', '')}${settings.banner_image_url}`}
                alt="Referral Banner"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        )}
        
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f5f3ff"/>
          </svg>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works?</h2>
          <p className="text-gray-600 max-w-xl mx-auto">Simple steps to start earning rewards</p>
        </div>
        
        <div className="grid md:grid-cols-4 gap-6">
          {(settings?.how_it_works || []).map((step, index) => (
            <div key={index} className="relative">
              <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg ${
                    index === 0 ? 'bg-gradient-to-br from-blue-500 to-indigo-600' :
                    index === 1 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                    index === 2 ? 'bg-gradient-to-br from-orange-500 to-red-600' :
                    'bg-gradient-to-br from-purple-500 to-pink-600'
                  }`}>
                    {index === 0 && <Share2 className="h-8 w-8 text-white" />}
                    {index === 1 && <UserPlus className="h-8 w-8 text-white" />}
                    {index === 2 && <Phone className="h-8 w-8 text-white" />}
                    {index === 3 && <Gift className="h-8 w-8 text-white" />}
                  </div>
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    {step.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
              {index < 3 && (
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <ChevronRight className="h-6 w-6 text-indigo-400" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Stats & Wallet Section (for logged in users) */}
      {isLoggedIn && wallet && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 border-0 text-white shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Available Balance</p>
                    <p className="text-3xl font-bold">₹{wallet.available_balance?.toFixed(2) || '0.00'}</p>
                  </div>
                  <Wallet className="h-10 w-10 text-white/60" />
                </div>
                <Button 
                  onClick={() => setWithdrawDialogOpen(true)}
                  disabled={wallet.available_balance < (settings?.min_withdrawal || 100)}
                  className="mt-4 w-full bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  Withdraw
                </Button>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Earned</p>
                    <p className="text-3xl font-bold text-green-600">₹{wallet.total_earned?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Referrals</p>
                    <p className="text-3xl font-bold text-blue-600">{wallet.total_referrals || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Successful</p>
                    <p className="text-3xl font-bold text-purple-600">{wallet.successful_referrals || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Award className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Referral History */}
          {referrals.length > 0 && (
            <Card className="border-0 shadow-lg mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-600" />
                  Your Referral History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 md:hidden" data-testid="referral-history-cards">
                  {referrals.map((ref) => (
                    <div key={ref.id} className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-gray-900">{ref.referred_name}</p>
                          <p className="truncate text-sm text-gray-500">{ref.referred_email}</p>
                        </div>
                        <Badge className={`shrink-0 ${rewardStatusColors[ref.reward_status] || 'bg-gray-100'}`}>
                          {ref.reward_status === 'Credited' ? `₹${ref.reward_amount}` : ref.reward_status}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                        <Badge className={statusColors[ref.status] || 'bg-gray-100'}>
                          {ref.status?.replace('_', ' ')}
                        </Badge>
                        {ref.job_title && <span className="truncate">{ref.job_title}</span>}
                        {ref.created_at && (
                          <span className="ml-auto text-xs text-gray-400">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Referred Person</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Position</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Reward</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((ref) => (
                        <tr key={ref.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{ref.referred_name}</p>
                              <p className="text-sm text-gray-500">{ref.referred_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600">{ref.job_title || '-'}</td>
                          <td className="py-3 px-4">
                            <Badge className={statusColors[ref.status] || 'bg-gray-100'}>
                              {ref.status?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={rewardStatusColors[ref.reward_status] || 'bg-gray-100'}>
                              {ref.reward_status === 'Credited' ? `₹${ref.reward_amount}` : ref.reward_status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm">
                            {ref.created_at && new Date(ref.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Leaderboard Section - Only show if enabled */}
      {settings?.show_leaderboard !== false && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Trophy className="h-7 w-7" />
                Top Referrers Leaderboard
              </CardTitle>
              <CardDescription className="text-white/80">
                Our highest earning referrers this month
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {leaderboard.length === 0 ? (
                <div className="p-12 text-center">
                  <Trophy className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No referrals yet. Be the first to top the leaderboard!</p>
                </div>
              ) : (
                <div className="divide-y">
                  {leaderboard.map((entry, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                        index === 0 ? 'bg-yellow-50' :
                        index === 1 ? 'bg-gray-50' :
                        index === 2 ? 'bg-orange-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-500 text-white' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          #{entry.rank}
                        </div>
                        <div>
                        <p className="font-semibold text-gray-900">{entry.user_name}</p>
                        <p className="text-sm text-gray-500">{entry.successful_referrals} successful referrals</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">₹{entry.total_earned?.toFixed(0)}</p>
                      <p className="text-xs text-gray-500">Total Earned</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Proven Methods Section */}
      <div className="bg-gradient-to-br from-indigo-900 to-purple-900 py-16 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Proven Methods to Get Referrals</h2>
            <p className="text-indigo-200">Tips from our top earners</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: MessageCircle, title: 'WhatsApp & Telegram', desc: 'Share your referral link on all WhatsApp & Telegram groups, especially college & job groups' },
              { icon: Users, title: 'Social Media', desc: 'Post your referral link on Facebook, Instagram, LinkedIn with engaging content' },
              { icon: Mail, title: 'Email Friends', desc: 'Send personalized emails to friends who are looking for job opportunities' },
              { icon: Share2, title: 'Create Content', desc: 'Make reels, stories about your experience and include your referral code' }
            ].map((tip, index) => (
              <Card key={index} className="bg-white/10 border-white/20 text-white backdrop-blur-sm">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <tip.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">{tip.title}</h3>
                    <p className="text-white/80 text-sm">{tip.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Terms & Conditions Section - Only show if either terms or policy exists */}
      {(settings?.terms_and_conditions || settings?.policy) && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Terms and Conditions</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <h4 className="font-semibold text-gray-900 mt-4">Definitions:</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Referrer:</strong> A person who invites their friend to Officium Connect using their referral code</li>
                <li><strong>Referred Friend:</strong> A person who signs up using a referral code</li>
                <li><strong>Successful Referral:</strong> When a referred friend clears the telephone round and gets selected</li>
              </ul>
              
              {settings?.terms_and_conditions && (
                <>
                  <h4 className="font-semibold text-gray-900 mt-4">Terms:</h4>
                  <div className="whitespace-pre-wrap text-sm">
                    {settings.terms_and_conditions}
                  </div>
                </>
              )}
              
              {settings?.policy && (
                <>
                  <h4 className="font-semibold text-gray-900 mt-4">Referral Policy:</h4>
                  <div className="whitespace-pre-wrap text-sm">
                    {settings.policy}
                  </div>
                </>
              )}
              
              <h4 className="font-semibold text-gray-900 mt-4">Important:</h4>
              <ul className="list-disc pl-5 space-y-1">
                {settings?.min_withdrawal > 0 && <li>Minimum withdrawal amount: ₹{settings.min_withdrawal}</li>}
                {settings?.max_monthly_rewards > 0 && <li>Maximum monthly rewards: ₹{settings.max_monthly_rewards}</li>}
                <li>Rewards are credited within 24-48 hours of approval</li>
                <li>Fraudulent activities will result in account suspension</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CTA Section */}
      {!isLoggedIn && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Earning?</h2>
            <p className="text-white/80 mb-8">Sign up now and get your unique referral code!</p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-white text-indigo-600 hover:bg-gray-100 gap-2 rounded-xl px-8 py-6 text-lg"
            >
              Get Started Now <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Withdrawal</DialogTitle>
            <DialogDescription>
              Enter the amount you want to withdraw (Min: ₹{settings?.min_withdrawal || 100})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Amount (₹)</label>
              <Input 
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount"
                max={wallet?.available_balance}
              />
              <p className="text-xs text-gray-500 mt-1">Available: ₹{wallet?.available_balance?.toFixed(2)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">UPI ID</label>
              <Input 
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="yourname@upi"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleWithdraw} disabled={withdrawing}>
              {withdrawing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
